import type { ReactNode } from "react"
import { useDispatch } from "react-redux"
import { Link, useLocation } from "react-router-dom"
import { LogOut, Menu, X } from "lucide-react"
import { useState } from "react"

import { useAuth } from "@/hooks/useAuth"
import { usePermissions } from "@/hooks/usePermissions"
import { logout } from "@/context/slices/authSlice"
import { navigationItems } from "@/routes"
import cogeserLogo from "@/assets/cogeser-logo.svg"

// ----------------------------------------------------------------------------

interface LayoutProps {
  children: ReactNode
}

export default function Layout({ children }: LayoutProps) {
  // Hooks
  const dispatch = useDispatch()
  const location = useLocation()
  const { user } = useAuth()
  const { role } = usePermissions()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const ROLE_HIERARCHY: Record<string, number> = { admin: 3, operator: 2, viewer: 1 }
  const userLevel = ROLE_HIERARCHY[role] || 0
  const visibleNavItems = navigationItems.filter((item) => {
    if (!item.requiredRole) return true
    return userLevel >= (ROLE_HIERARCHY[item.requiredRole] || 0)
  })

  // Component functions
  function handleLogout() {
    dispatch(logout())
  }

  function isActive(url: string) {
    if (url === "/") return location.pathname === "/"
    return location.pathname.startsWith(url)
  }

  // Component local variables

  return (
    <div className="flex h-screen bg-gray-50/80">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 flex w-64 flex-col bg-white shadow-lg transition-transform lg:static lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Logo — green gradient header */}
        <div className="relative overflow-hidden bg-gradient-to-r from-[#006838] to-[#3a9c4d] px-5 py-5">
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M20 18v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
          <div className="relative flex items-center justify-between">
            <img src={cogeserLogo} alt="Cogeser" className="h-10 drop-shadow" />
            <button
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5 text-white/80" />
            </button>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-1 px-3 py-4">
          {visibleNavItems.map((item) => {
            const Icon = item.icon
            const active = isActive(item.url)
            return (
              <Link
                key={item.url}
                to={item.url}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all ${
                  active
                    ? "bg-gradient-to-r from-primary/10 to-primary/5 text-primary shadow-sm"
                    : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                }`}
              >
                <Icon className={`h-5 w-5 ${active ? "text-primary" : ""}`} />
                {item.title}
              </Link>
            )
          })}
        </nav>

        {/* User section */}
        <div className="border-t border-gray-100 p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-[#006838] to-[#3a9c4d] text-xs font-semibold text-white shadow-sm">
              {user?.displayName?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 overflow-hidden">
              <p className="truncate text-sm font-medium text-gray-900">
                {user?.displayName || "Admin"}
              </p>
              <p className="truncate text-xs text-gray-500">{user?.email}</p>
            </div>
            <button
              onClick={handleLogout}
              className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500"
              title="Logout"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Header */}
        <header className="flex h-14 items-center gap-4 border-b border-gray-200/60 bg-white/80 px-6 backdrop-blur-sm">
          <button
            className="lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-5 w-5 text-gray-600" />
          </button>
          <div className="flex-1" />
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-6">
          <div className="mx-auto max-w-7xl">{children}</div>
        </main>
      </div>
    </div>
  )
}
